/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\DynamicContextUploadModal.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the AI Integration module.
----------------------------------------------------------------*/


import { useState } from 'react';
import { X, FileUp, Eye, RefreshCw, Upload } from 'lucide-react';
import { Label } from '../../../components/ui/label';

interface DynamicContextUploadModalProps {
  isOpen: boolean;
  selectedFile: File | null;
  extractionPrompt: string;
  uploading: boolean;
  setExtractionPrompt: (v: string) => void;
  onUpload: () => void;
  onClose: () => void;
  t: (key: string, fallback?: string) => string;
}

export function DynamicContextUploadModal({
  isOpen, selectedFile, extractionPrompt, uploading,
  setExtractionPrompt, onUpload, onClose, t,
}: DynamicContextUploadModalProps) {
  const [showPromptEditor, setShowPromptEditor] = useState(false);

  if (!isOpen || !selectedFile) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-card border border-border rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h2 className="text-lg font-semibold">
            {t('aiAgents.dynamicContext.uploadDocument') || 'Upload Document'}
          </h2>
          <button type="button" onClick={onClose} className="p-1 hover:bg-muted rounded">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-4">
          {/* File Info */}
          <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
            <FileUp className="h-8 w-8 text-primary" />
            <div>
              <p className="font-medium">{selectedFile.name}</p>
              <p className="text-sm text-muted-foreground">
                {(selectedFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>

          {/* Extraction Prompt */}
          <div>
            <div className="flex items-center justify-between">
              <Label>{t('aiAgents.dynamicContext.extractionPrompt') || 'Extraction Prompt'}</Label>
              <button
                type="button"
                onClick={() => setShowPromptEditor(p => !p)}
                className="text-xs text-primary hover:underline flex items-center gap-1"
              >
                <Eye className="h-3 w-3" />
                {showPromptEditor ? 'Hide' : 'View/Edit'}
              </button>
            </div>
            {showPromptEditor ? (
              <textarea
                value={extractionPrompt}
                onChange={e => setExtractionPrompt(e.target.value)}
                className="w-full mt-2 px-3 py-2 bg-background border border-input rounded-md h-48 font-mono text-xs resize-y"
              />
            ) : (
              <p className="text-sm text-muted-foreground mt-1">
                AI will analyze the document and extract context blocks automatically.
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-4 border-t border-border">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm hover:bg-muted rounded-md">
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            type="button"
            onClick={onUpload}
            disabled={uploading}
            className="px-4 py-2 text-sm bg-primary text-primary-foreground hover:bg-primary/90 rounded-md flex items-center gap-2"
          >
            {uploading ? (
              <>
                <RefreshCw className="h-4 w-4 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4" />
                Upload &amp; Process
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
